import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import type {
  FactCheckProject,
  InvestigationItem,
  InvestigationMessage,
  UserSettings,
} from '../types';
import { calculateVerificationStats } from '../utils/statsUtils';

// Firestoreコレクション名
const PROJECTS_COLLECTION = 'projects';
const USERS_COLLECTION = 'users';

// ===== エラーハンドリング =====
class FirestoreError extends Error {
  public code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'FirestoreError';
    this.code = code;
  }
}

// ===== ユーザー管理 =====
export const createUserProfile = async (
  userId: string,
  userData: {
    email: string;
    displayName?: string;
    photoURL?: string;
    provider: string;
  },
): Promise<void> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await setDoc(userRef, {
      ...userData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw new FirestoreError('ユーザープロファイルの作成に失敗しました');
  }
};

export const getUserProfile = async (userId: string) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);
    return userSnap.exists() ? userSnap.data() : null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw new FirestoreError('ユーザープロファイルの取得に失敗しました');
  }
};

// ===== プロジェクト管理 =====

export const getAllProjects = async (userId: string): Promise<FactCheckProject[]> => {
  try {
    const projectsRef = collection(db, PROJECTS_COLLECTION);
    const q = query(projectsRef, where('userId', '==', userId));

    const querySnapshot = await getDocs(q);
    const projects = querySnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as FactCheckProject,
    );

    // クライアント側でソート（インデックス不要）
    return projects.sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
  } catch (error) {
    console.error('Error getting projects:', error);
    throw new FirestoreError('プロジェクトの取得に失敗しました');
  }
};

export const getProjectById = async (
  userId: string,
  projectId: string,
): Promise<FactCheckProject | null> => {
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    const projectSnap = await getDoc(projectRef);

    if (!projectSnap.exists()) {
      return null;
    }

    const projectData = projectSnap.data() as FactCheckProject;

    // セキュリティチェック: ユーザーのプロジェクトかどうか確認
    if (projectData.userId !== userId) {
      throw new FirestoreError('アクセス権限がありません');
    }

    return {
      ...projectData,
      id: projectSnap.id,
    };
  } catch (error) {
    console.error('Error getting project:', error);
    if (error instanceof FirestoreError) {
      throw error;
    }
    throw new FirestoreError('プロジェクトの取得に失敗しました');
  }
};

export const saveProject = async (project: FactCheckProject): Promise<void> => {
  try {
    const projectData = {
      ...project,
      updated_at: new Date().toISOString(),
    };

    // 新規プロジェクト作成：session_idをドキュメントIDとして使用
    const projectRef = doc(db, PROJECTS_COLLECTION, project.id);

    // setDoc を使用してドキュメントを作成または更新
    await setDoc(projectRef, {
      ...projectData,
      created_at: project.created_at || new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error saving project:', error);
    throw new FirestoreError('プロジェクトの保存に失敗しました');
  }
};

export const deleteProject = async (userId: string, projectId: string): Promise<void> => {
  try {
    // セキュリティチェック: プロジェクトの所有者確認
    const project = await getProjectById(userId, projectId);
    if (!project) {
      throw new FirestoreError('プロジェクトが見つかりません');
    }

    // 1. バックエンドでGCS PDFファイルとセッションデータを削除
    try {
      const url = new URL(`/api/projects/${projectId}`, window.location.origin);

      // ブロブ名がある場合はクエリパラメータで送信
      if (project.pdf_blob_name) {
        url.searchParams.set('pdf_blob_name', project.pdf_blob_name);
      }

      const headers: Record<string, string> = {};
      const currentUser = auth.currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url.toString(), {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        console.warn('GCS PDF削除に失敗しました:', await response.text());
        // GCS削除失敗でもFirestore削除は続行
      } else {
        const data = await response.json();
        console.log('✅ GCS PDF削除成功:', data.message);
      }
    } catch (gcsError) {
      console.warn('GCS PDF削除リクエスト失敗:', gcsError);
      // GCS削除失敗でもFirestore削除は続行
    }

    // 2. Firestoreからプロジェクトレコードを削除
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    await deleteDoc(projectRef);

    console.log('✅ プロジェクト削除完了 - Firestore + GCS');
  } catch (error) {
    console.error('Error deleting project:', error);
    if (error instanceof FirestoreError) {
      throw error;
    }
    throw new FirestoreError('プロジェクトの削除に失敗しました');
  }
};

export const searchProjects = async (
  userId: string,
  searchQuery: string,
): Promise<FactCheckProject[]> => {
  try {
    // Firestoreの全文検索は制限があるため、全プロジェクトを取得してクライアント側でフィルタ
    const projects = await getAllProjects(userId);
    const lowerQuery = searchQuery.toLowerCase();

    return projects.filter(
      (project) =>
        project.title.toLowerCase().includes(lowerQuery) ||
        project.description?.toLowerCase().includes(lowerQuery) ||
        project.pdf_filename.toLowerCase().includes(lowerQuery),
    );
  } catch (error) {
    console.error('Error searching projects:', error);
    throw new FirestoreError('プロジェクトの検索に失敗しました');
  }
};

export const filterProjectsByStatus = async (
  userId: string,
  status: 'all' | 'extracted' | 'completed',
): Promise<FactCheckProject[]> => {
  try {
    // 全プロジェクトを取得してクライアント側でフィルタ（インデックス不要）
    const projects = await getAllProjects(userId);

    if (status === 'all') {
      return projects;
    }

    return projects.filter((project) => project.status === status);
  } catch (error) {
    console.error('Error filtering projects:', error);
    throw new FirestoreError('プロジェクトのフィルタリングに失敗しました');
  }
};

// ===== 統計情報 =====

export const getDashboardStats = async (userId: string) => {
  try {
    const projects = await getAllProjects(userId);
    const completedProjects = projects.filter((p) => p.status === 'completed');

    const totalFactsVerified = completedProjects.reduce(
      (sum, project) => sum + project.verified_facts,
      0,
    );

    // accuracy_rateは削除されたので、verification_resultsから動的計算
    const averageAccuracy =
      completedProjects.length > 0
        ? completedProjects.reduce((sum, project) => {
            if (project.verification_results && project.verification_results.length > 0) {
              const stats = calculateVerificationStats(project.verification_results);
              const total =
                stats.accurate + stats.partiallyAccurate + stats.inaccurate + stats.unverifiable;
              return sum + (total > 0 ? (stats.accurate / total) * 100 : 0);
            }
            return sum;
          }, 0) /
          completedProjects.filter(
            (p) => p.verification_results && p.verification_results.length > 0,
          ).length
        : 0;

    return {
      total_projects: projects.length,
      completed_projects: completedProjects.length,
      total_facts_verified: totalFactsVerified,
      average_accuracy: Math.round((averageAccuracy || 0) * 100) / 100,
    };
  } catch (error) {
    console.error('Error getting dashboard stats:', error);
    throw new FirestoreError('統計情報の取得に失敗しました');
  }
};

// ===== 設定管理 =====

const DEFAULT_SETTINGS: UserSettings = {
  theme: 'light',
  default_verification_strategy: 'google_search',
  auto_save: true,
};

export const getUserSettings = async (userId: string): Promise<UserSettings> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();
      return userData.settings || DEFAULT_SETTINGS;
    }

    return DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error getting user settings:', error);
    return DEFAULT_SETTINGS;
  }
};

export const saveUserSettings = async (userId: string, settings: UserSettings): Promise<void> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      settings,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error saving user settings:', error);
    throw new FirestoreError('設定の保存に失敗しました');
  }
};

// ===== オフライン対応 =====

export const enableOfflineSupport = () => {
  try {
    // Firestore v9では自動的にオフライン対応が有効
    console.log('Firestore offline support enabled');
  } catch (error) {
    console.error('Error enabling offline support:', error);
  }
};

// ===== バッチ操作（将来の拡張用） =====

export const batchUpdateProjects = async (
  updates: Array<{ id: string; data: Partial<FactCheckProject> }>,
) => {
  // バッチ操作の実装（必要に応じて）
  console.log('Batch updates:', updates);
};

// ===== プロジェクト更新機能 =====

export const updateProject = async (
  userId: string,
  projectId: string,
  updates: Partial<FactCheckProject>,
): Promise<void> => {
  try {
    // セキュリティチェック: プロジェクトの所有者確認
    const existingProject = await getProjectById(userId, projectId);
    if (!existingProject) {
      throw new FirestoreError('プロジェクトが見つかりません');
    }

    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    await updateDoc(projectRef, {
      ...updates,
      updated_at: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating project:', error);
    if (error instanceof FirestoreError) {
      throw error;
    }
    throw new FirestoreError('プロジェクトの更新に失敗しました');
  }
};

// ===== 統合調査アイテム管理 =====

const INVESTIGATION_ITEMS_COLLECTION = 'investigation_items';

export const addInvestigationItem = async (
  userId: string,
  projectData: FactCheckProject,
  factIndex: number,
  factData: { claim: string; result: string; confidence: number; verification_summary: string },
): Promise<string> => {
  try {
    const itemId = `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const now = new Date().toISOString();

    const investigationItem: InvestigationItem = {
      id: itemId,
      userId: userId,
      project_id: projectData.id,
      project_title: projectData.title,
      fact_index: factIndex,
      claim: factData.claim,
      verification_result: factData.result,
      verification_summary: factData.verification_summary,
      confidence: factData.confidence,
      added_at: now,
      status: 'waiting',
      updated_at: now,
    };

    const itemRef = doc(db, INVESTIGATION_ITEMS_COLLECTION, itemId);
    await setDoc(itemRef, investigationItem);

    return itemId;
  } catch (error) {
    console.error('Error adding investigation item:', error);
    throw new FirestoreError('調査アイテムの追加に失敗しました');
  }
};

export const updateInvestigationMessage = async (
  itemId: string,
  messageId: string,
  newContent: string,
): Promise<void> => {
  try {
    const itemRef = doc(db, INVESTIGATION_ITEMS_COLLECTION, itemId);
    const itemSnap = await getDoc(itemRef);

    if (!itemSnap.exists()) {
      throw new Error(`Investigation item with ID ${itemId} not found`);
    }

    const item = itemSnap.data() as InvestigationItem;
    const messages = item.messages || [];
    const messageIndex = messages.findIndex((msg) => msg.id === messageId);

    if (messageIndex === -1) {
      throw new Error(`Message with ID ${messageId} not found in item ${itemId}`);
    }

    messages[messageIndex].content = newContent;
    messages[messageIndex].updated_at = new Date().toISOString();

    await updateDoc(itemRef, { messages });
  } catch (error) {
    console.error('Error updating investigation message:', error);
    throw new FirestoreError('メッセージの更新に失敗しました');
  }
};

export const getInvestigationItems = async (
  userId: string,
  status?: 'waiting' | 'investigating',
): Promise<InvestigationItem[]> => {
  try {
    const itemsQuery = status
      ? query(
          collection(db, INVESTIGATION_ITEMS_COLLECTION),
          where('userId', '==', userId),
          where('status', '==', status),
        )
      : query(collection(db, INVESTIGATION_ITEMS_COLLECTION), where('userId', '==', userId));

    const querySnapshot = await getDocs(itemsQuery);
    const items = querySnapshot.docs.map((doc) => doc.data() as InvestigationItem);

    // 更新日順でソート（新しい順）
    return items.sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
  } catch (error) {
    console.error('Error getting investigation items:', error);
    throw new FirestoreError('調査アイテムの取得に失敗しました');
  }
};

export const getInvestigationItem = async (itemId: string): Promise<InvestigationItem | null> => {
  try {
    const itemRef = doc(db, INVESTIGATION_ITEMS_COLLECTION, itemId);
    const itemSnap = await getDoc(itemRef);

    if (itemSnap.exists()) {
      return itemSnap.data() as InvestigationItem;
    }

    return null;
  } catch (error) {
    console.error('Error getting investigation item:', error);
    throw new FirestoreError('調査アイテムの取得に失敗しました');
  }
};

export const startInvestigation = async (itemId: string, title?: string): Promise<void> => {
  try {
    const itemRef = doc(db, INVESTIGATION_ITEMS_COLLECTION, itemId);
    const now = new Date().toISOString();

    await updateDoc(itemRef, {
      status: 'investigating',
      investigation_started_at: now,
      updated_at: now,
      title: title || 'investigation session',
      messages: [],
      message_count: 0,
    });
  } catch (error) {
    console.error('Error starting investigation:', error);
    throw new FirestoreError('調査の開始に失敗しました');
  }
};

export const addMessageToInvestigationItem = async (
  itemId: string,
  message: InvestigationMessage,
): Promise<void> => {
  try {
    const itemRef = doc(db, INVESTIGATION_ITEMS_COLLECTION, itemId);

    // アイテムが存在するか確認
    const itemSnap = await getDoc(itemRef);
    if (!itemSnap.exists()) {
      throw new FirestoreError('調査アイテムが見つかりません');
    }

    const currentData = itemSnap.data() as InvestigationItem;
    let updatedMessages = [...(currentData.messages || []), message];

    // ADK推奨パターン: 30ターン上限でメッセージ管理
    // ユーザー入力 + エージェント回答を1ターンとしてカウント
    const MAX_TURNS = 30;
    const messagesPerTurn = 2; // user + assistant
    const maxMessages = MAX_TURNS * messagesPerTurn;

    // メッセージが上限を超えた場合、古いターンを削除
    if (updatedMessages.length > maxMessages) {
      const messagesToRemove = updatedMessages.length - maxMessages;
      // ターン単位で削除（ペア単位で削除して整合性を保つ）
      const turnsToRemove = Math.ceil(messagesToRemove / messagesPerTurn);
      const messagesToRemoveCount = turnsToRemove * messagesPerTurn;
      updatedMessages = updatedMessages.slice(messagesToRemoveCount);
    }

    // アイテムタイトルが初期値の場合、最初のユーザーメッセージから生成
    let newTitle = currentData.title;
    if ((!newTitle || newTitle === 'investigation session') && message.role === 'user') {
      newTitle = message.content.substring(0, 50) + (message.content.length > 50 ? '...' : '');
    }

    await updateDoc(itemRef, {
      messages: updatedMessages,
      message_count: updatedMessages.length,
      title: newTitle,
      updated_at: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error adding message to investigation item:', error);
    if (error instanceof FirestoreError) {
      throw error;
    }
    throw new FirestoreError('調査アイテムへのメッセージ追加に失敗しました');
  }
};
